import React, { useEffect, useState, useRef } from 'react';
import { SideSheet, OTPVerificationSideSheet, SnackBar } from '@desquared/wave-components-library';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';

import { OTPVerificationSideSheetStore } from '@/store/sideSheet';
import { OTPIDStore } from '@/store/otpid';
import { verifyOTP, resendOTP } from '@/api/register';
import { formatTime } from '@/utils/transformers';
import User from '@/store/user';
import EmailVerification from '../../../public/assets/wave-grocery/EmailVerification.png';
import PhoneVerification from '../../../public/assets/wave-grocery/PhoneVerification.png';
import { updateUser as updateUserInContactPigeon } from '@/utils/contactPigeon';

import styles from './CodeVerificationSideSheet.module.css';

export default function CodeVerificationSideSheet({ user, userDetails, config, resource }) {
  const [sideSheet, setSideSheet] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [otpValue, setOtpValue] = useState('');
  const [hasBottomError, setHasBottomError] = useState(false);
  const [bottomErrorText, setBottomErrorText] = useState('');
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackBarText, setSnackBarText] = useState('');
  const [timeLeft, setTimeLeft] = useState();
  const [resendDisabled, setResendDisabled] = useState(false);
  const [totalTimeLeft, setTotalTimeLeft] = useState(config.auth.verification.registration.otp.validForSeconds);
  const [timerOTP, setTimerOTP] = useState(false);
  const [timerOTPText, setTimerOTPText] = useState(false);
  const router = useRouter();

  const refOne = useRef(null);
  const t = useTranslations('');
  const onClick = () => {
    setTimeout(() => {
      OTPVerificationSideSheetStore.close();
    }, '300');
  };
  useEffect(() => {
    const OTPVerificationSideSheetStore$ = OTPVerificationSideSheetStore.subscribe(setSideSheet);

    document.addEventListener('click', handleClickOutside, true);

    return () => {
      OTPVerificationSideSheetStore$.unsubscribe();

      document.removeEventListener('click', handleClickOutside, false);
    };
  }, []);
  const otpInfo = OTPIDStore.getValue();

  useEffect(() => {
    if (timeLeft === 0) {
      setResendDisabled(false);
      setSnackBarOpen(false);
      return;
    }
    const timer = setInterval(() => {
      setTimeLeft(prevTime => prevTime - 1);
      setSnackBarText(`${t('user_credentials.verification.add_code_again')} ${formatTime(timeLeft)}’`);
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  useEffect(() => {
    if (totalTimeLeft === 0) {
      OTPVerificationSideSheetStore.close();
      return;
    }
    if (totalTimeLeft === 120) {
      setTimerOTPText(`${t('user_credentials.verification.ends_in')} ${formatTime(totalTimeLeft)}’`);
      setTimerOTP(true);
    }

    const timer = setInterval(() => {
      setTotalTimeLeft(prevTime => prevTime - 1);
      if (totalTimeLeft < 120) {
        setTimerOTPText(`${t('user_credentials.verification.ends_in')} ${formatTime(totalTimeLeft)}’`);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [totalTimeLeft]);

  const handleClickOutside = e => {
    if (refOne.current && !refOne.current.contains(e.target)) {
      OTPVerificationSideSheetStore.close();
    }
  };

  const onOTPChange = e => {
    setOtpValue(e);
    setErrorText('');
    setHasBottomError(false);
    setBottomErrorText('');
  };

  const submit = async () => {
    const { data, error } = await verifyOTP({ requestId: otpInfo.requestId, otp: otpValue }, router.locale);
    if (error) {
      if (error.code === 'OTP0003') {
        setErrorText(error.userMessage);
        return;
      } else if (error.code === 'OTP0002') {
        setErrorText(error.userMessage);
        return;
      }
      setHasBottomError(true);
      setBottomErrorText(error.userMessage);
    } else {
      OTPIDStore.update({ ...otpInfo, otp: otpValue });
      User.update({ ...user, ...data.user });
      if (process.env.hasContactPigeon) {
        updateUserInContactPigeon(data.user.email);
      }
      OTPVerificationSideSheetStore.close();
      router.reload();
    }
  };

  const resend = async () => {
    setResendDisabled(true);
    const { data, error } = await resendOTP(
      { requestId: otpInfo.requestId, verificationCase: 'VERIFY' },
      router.locale
    );
    if (error) {
    } else {
      OTPIDStore.update({ requestId: data.otpInfo.requestId, email: user.email });
      setTimeLeft(config.auth.verification.forgotPassword.otp.resendAllowedInSeconds);
      setSnackBarText(
        `${t('user_credentials.verification.add_code_again')} ${formatTime(
          config.auth.verification.forgotPassword.otp.resendAllowedInSeconds
        )}’`
      );
      setSnackBarOpen(true);
    }
  };

  if (!sideSheet) return null;
  return (
    <div ref={refOne}>
      <SideSheet hasClose={true} onClose={onClick} position="right">
        <OTPVerificationSideSheet
          errorText={errorText}
          otpInputsValues={otpValue}
          onChangeOTP={onOTPChange}
          phoneNumber={resource === 'PHONE' ? user.mobilePhone : ''}
          title={resource === 'EMAIL' ? t('user_credentials.verification.email_confirmation') : ''}
          email={resource === 'EMAIL' ? ' ' + userDetails.email : ''}
          subTitle={
            resource === 'EMAIL'
              ? t('user_credentials.verification.otp_email_description')
              : t('user_credentials.verification.otp_phone_description')
          }
          image={resource === 'EMAIL' ? EmailVerification.src : PhoneVerification.src}
          displayName="password verification image"
          onSubmitForm={submit}
          onCancel={resend}
          submitText={t('user_credentials.verification.continue')}
          cancelText={t('user_credentials.verification.send_again')}
          otpPattern={config.auth.verification.registration.otp.pattern}
          hasBottomError={hasBottomError}
          bottomErrorText={bottomErrorText}
          hasBottomSuccess={false}
          hasAlert={timerOTP}
          alertText={timerOTPText}
          isResendDisabled={resendDisabled}
        />
      </SideSheet>
      {/* {timerModal && (
        <GenericModal
          icon={Timer}
          title="Το session επαλήθευσης του email λήγει σύντομα"
          description="Θα χρειαστεί να συμπληρωθεί ο κωδικός OTP που έχετε λάβει στα επόμενα 2’. Διαφορετικά θα γίνει ανακατεύθυνση στην σελίδα Σύνδεσης. "
          cancelWording=""
          onPrimaryClick={() => setTimerModalOpen(false)}
          primaryButtonWording="Κλείσιμο"
        />
      )} */}
      <div style={{ width: '100%' }}>
        <SnackBar
          open={snackBarOpen}
          setOpen={() => setSnackBarOpen(false)}
          text={snackBarText}
          hasCloseOption={true}
          className={styles.snackBarContainer}
          position={'center'}
        />
      </div>
    </div>
  );
}
